import { useState } from 'react';
import * as theme from '../../../theme';
import Heading from '../../../components/Heading';
import { addComment, editComment, deleteComment, markCommentsAsRead } from '../../../functions/commentUtils';
import { v4 as uuidv4 } from 'uuid';
import { sendEmail } from "../../../functions/endpoints/SendEmail";
import TruCard from '../../../components/TruCard';
import { Button, CircularProgress, IconButton, TextField, Box, Avatar } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import tbIcon from '../../../images/tb-icon.png';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

export default function ScanComments({ scan, setScan, practitionerId, clientId, scanId, currentUser, setError, clientName }) {
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState('');
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isEditingComment, setIsEditingComment] = useState(false);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setIsAddingComment(true);

    try {
      const comment = {
        id: uuidv4(),
        text: newComment,
        role: currentUser.role,
        displayName: currentUser.role === 'practitioner' ? currentUser.firstName : 'T',
      };
      const updatedScan = await addComment({
        clientId,
        scanId,
        scan,
        comment,
        currentUser,
        markReadForOppositeRoles: true,
      });
      setScan(updatedScan);
      setNewComment('');
      setShowCommentInput(false);

      // Send notification email
      let recipientEmail;
      let recipientName;
      if (currentUser.role === 'practitioner') {
        recipientEmail = process.env.REACT_APP_ENVIRONMENT === 'PROD' ? 'hello@trubalancebioenergetics.com' : "jhatlestad@gmail.com";
        recipientName = "TruBalance Admin";
      } else {
        const practRef = doc(db, 'practitioners', practitionerId); // Assuming scan has practitionerId; adjust if needed
        const practSnap = await getDoc(practRef);
        const practData = practSnap.data();
        recipientEmail = practData.email;
        recipientName = `${practData.firstName} ${practData.lastName}`;
      }

      const fromName = currentUser.role === 'practitioner' ? comment.displayName : 'The TruBalance Team';

      const emailData = {
        recipientEmail,
        recipientName,
        subject: "New Comment on Client Scan",
        templateId: "scan-comment-added",
        dynamicTemplateData: {
          fromName,
          clientName: clientName
        },
        emailType: "scan_comment_added",
        contextData: {
          scanId,
          commentId: comment.id,
        },
        userId: currentUser.uid,
        isTest: false
      };

      try {
        await sendEmail(emailData);
      } catch (emailErr) {
        console.error('Failed to send email:', emailErr);
      }
    } catch (err) {
      setError('Failed to add comment: ' + err.message);
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleEditComment = (comment) => {
    if (
      (currentUser.role === 'admin' && comment.role === 'admin') ||
      (currentUser.role === 'manager' && comment.role === 'manager') ||
      (currentUser.role === 'practitioner' && comment.role === 'practitioner')
    ) {
      setEditingCommentId(comment.id);
      setEditedCommentText(comment.text);
    }
  };

  const handleSaveEdit = async (comment) => {
    if (
      (currentUser.role === 'manager' && comment.role !== 'manager') ||
      (currentUser.role === 'practitioner' && comment.role !== 'practitioner')
    ) {
      return;
    }
    try {
      setIsEditingComment(true);
      const updatedScan = await editComment({
        clientId,
        scanId,
        scan,
        commentId: comment.id,
        newText: editedCommentText,
        currentUser,
      });
      setScan(updatedScan);
      setEditingCommentId(null);
      setEditedCommentText('');
    } catch (err) {
      setError('Failed to edit comment: ' + err.message);
    } finally {
      setIsEditingComment(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    if (currentUser.uid !== comment.createdBy) return;
    try {
      const updatedScan = await deleteComment({
        clientId,
        scanId,
        scan,
        comment,
        currentUser,
      });
      setScan(updatedScan);
      setEditingCommentId(null);
    } catch (err) {
      setError('Failed to delete comment: ' + err.message);
    }
  };

  const handleMarkAsRead = async () => {
    try {
      const updatedScan = await markCommentsAsRead({
        clientId,
        scanId,
        scan,
        currentUser,
      });
      setScan(updatedScan);
    } catch (err) {
      setError('Failed to mark comments as read: ' + err.message);
    }
  };

  const toggleCommentInput = () => {
    setShowCommentInput(!showCommentInput);
    if (showCommentInput) setNewComment('');
  };

  const hasCommentsToRead = scan.comments?.some(
    (comment) =>
      comment.markAsRead === false &&
      ((currentUser.role === 'practitioner' &&
        ['admin', 'manager'].includes(comment.role)) ||
        (['admin', 'manager'].includes(currentUser.role) &&
          comment.role === 'practitioner'))
  );

  return (
    <>
      <Heading
        title={'Comments'}
        subHeading={true}
        addMargin={true}
        icon={showCommentInput ? 'remove' : 'add'}
        iconAction={toggleCommentInput}
      />
      {showCommentInput && (
        <Box sx={{ marginBottom: 2 }}>
          <TextField
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder='Add a comment...'
            variant='outlined'
            fullWidth
            size='small'
            multiline
            rows={3}
          />
          <Box sx={{ marginTop: 1, display: 'flex', justifyContent: 'right', gap: 1 }}>
            <Button
              onClick={toggleCommentInput}
              variant='contained'
              color='secondary'
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddComment}
              variant='contained'
              color='primary'
              disabled={!newComment.trim() || isAddingComment}
              startIcon={isAddingComment ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {isAddingComment ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      )}
      <div style={theme.Container.flexOne}>
        <TruCard title={'Comments'}>
          <div style={theme.TruCard.SubTextLeft}>
            {scan.comments && scan.comments.length > 0 ? (
              scan.comments.map((comment) => (
                <li
                  key={comment.id}
                  style={{
                    marginBottom: 15,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                  }}
                >
                  <IconButton onClick={() => handleEditComment(comment)}>
                    <Avatar
                      sx={{ bgcolor: theme.Colors.Purple }}
                      src={comment.role !== 'practitioner' ? tbIcon : null}
                    >
                      {comment.role === 'practitioner'
                        ? comment.displayName?.slice(0, 1)
                        : null}
                    </Avatar>
                  </IconButton>
                  <div style={{ flex: 1 }}>
                    {editingCommentId === comment.id ? (
                      <Box>
                        <TextField
                          value={editedCommentText}
                          onChange={(e) => setEditedCommentText(e.target.value)}
                          fullWidth
                          size='small'
                          multiline
                          rows={3}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'right',
                            width: '100%',
                            marginTop: 1,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'left',
                              flex: 1,
                            }}
                          >
                            {currentUser.uid === comment.createdBy && (
                              <IconButton
                                edge="end"
                                onClick={() => handleDeleteComment(comment)}
                                color='secondary'
                                size='small'
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'right',
                              flex: 2,
                            }}
                          >
                            <Button
                              onClick={() => setEditingCommentId(null)}
                              variant='contained'
                              color='secondary'
                              size='small'
                              sx={{ marginRight: 1 }}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleSaveEdit(comment)}
                              variant='contained'
                              color='primary'
                              size='small'
                              disabled={isEditingComment}
                              startIcon={isEditingComment ? <CircularProgress size={14} color="inherit" /> : null}
                            >
                              {isEditingComment ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <div style={{ fontSize: 14 }}>{comment.text}</div>
                        <div
                          style={{
                            fontSize: '0.8em',
                            color: theme.Colors.GrayMedium,
                            marginTop: 0,
                            marginBottom: 4,
                          }}
                        >
                          {dayjs(comment.dateCreated.toDate()).format(
                            'MM/DD/YYYY hh:mm A'
                          )}{' '}
                          by{' '}
                          {comment.role === 'practitioner' &&
                          comment.displayName
                            ? comment.displayName
                            : comment.role === 'practitioner'
                            ? comment.role
                            : 'TruBalance Team'}
                        </div>
                      </>
                    )}
                  </div>
                </li>
              ))
            ) : (
              <div>No comments available.</div>
            )}
          </div>
          {hasCommentsToRead && (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
              <Button
                variant='contained'
                color='primary'
                onClick={handleMarkAsRead}
              >
                Mark As Read
              </Button>
            </Box>
          )}
        </TruCard>
      </div>
    </>
  );
}